
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Newspaper } from 'lucide-react';
import { NewsArticle } from '@/types/news';
import NewsArticleCard from '@/components/news/NewsArticleCard';

interface NewsGridProps {
  isLoading: boolean;
  isError: boolean;
  articles: NewsArticle[];
  onLoadMore: () => void;
  totalArticles: number;
  currentPage: number;
  itemsPerPage: number;
}

const NewsGrid: React.FC<NewsGridProps> = ({ 
  isLoading, 
  isError, 
  articles, 
  onLoadMore,
  totalArticles,
  currentPage,
  itemsPerPage
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={`skeleton-${i}`}>
            <Skeleton className="h-[200px] w-full rounded-t-lg" />
            <CardContent className="p-4 space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 mb-4">Failed to load news articles.</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <div className="text-center py-10">
        <Newspaper className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <p className="text-xl font-medium mb-2">No articles found</p>
        <p className="text-muted-foreground mb-4">
          Try adjusting your filters or check back later for new content.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map(article => (
          <NewsArticleCard 
            key={article.id} 
            article={article} 
          />
        ))}
      </div>
      
      {totalArticles > currentPage * itemsPerPage && (
        <div className="flex justify-center mt-8">
          <Button onClick={onLoadMore} variant="outline" size="lg">
            Load More Articles
          </Button>
        </div>
      )}
    </>
  );
};

export default NewsGrid;
